import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { API_CONFIG, ApiConfig } from '../../api-config';

export interface CompanyServiceShippingMethodSelectOption {
  value: string;
  label: string;
}

/**
 * Formas de envío del servicio en la empresa (subtab Forma de envío).
 * Fuente del multiselect de Estatus.
 *
 * GET {apiUrl}/selects/company-service-shipping-methods?companyId=&serviceId=
 */
@Injectable({ providedIn: 'root' })
export class CompanyServiceShippingMethodsSelectService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);
  private readonly cache = new Map<string, CompanyServiceShippingMethodSelectOption[]>();
  private readonly endpointUrl = '/selects/company-service-shipping-methods';

  getOptions(
    companyId: string,
    serviceId: string,
  ): Observable<CompanyServiceShippingMethodSelectOption[]> {
    const company = companyId.trim();
    const service = serviceId.trim();
    if (!company || !service) return of([]);

    const cacheKey = `${company}|${service}`;
    const cached = this.cache.get(cacheKey);
    if (cached) return of(cached);

    return this.httpClient
      .get(`${this.apiConfig.apiUrl}${this.endpointUrl}`, {
        params: { companyId: company, serviceId: service },
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        map((resp) => this.parseSelectResponse(resp.body)),
        tap((options) => this.cache.set(cacheKey, options)),
        catchError(() => of([])),
      );
  }

  /** Invalida cache tras guardar el drawer Configurar. */
  invalidate(companyId?: string, serviceId?: string): void {
    if (!companyId?.trim()) {
      this.cache.clear();
      return;
    }
    const company = companyId.trim();
    if (!serviceId?.trim()) {
      for (const key of [...this.cache.keys()]) {
        if (key.startsWith(`${company}|`)) this.cache.delete(key);
      }
      return;
    }
    this.cache.delete(`${company}|${serviceId.trim()}`);
  }

  private parseSelectResponse(
    body: unknown,
  ): CompanyServiceShippingMethodSelectOption[] {
    if (typeof body !== 'string' || !body) return [];
    try {
      const parsed = JSON.parse(body) as {
        success?: boolean;
        data?: Array<{ id: string; text?: string; label?: string; name?: string }>;
      };
      if (!parsed || parsed.success !== true || !Array.isArray(parsed.data)) {
        return [];
      }
      return parsed.data.map((item) => ({
        value: item.id || '',
        label: item.text || item.label || item.name || '',
      }));
    } catch {
      return [];
    }
  }
}
